import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Youtube, Linkedin } from 'lucide-react';
import { companyInfo, quickLinks, productCategories } from '../../data/mockData';
import { useLanguage } from '../../contexts/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="bg-[#1a2942] text-white">
      <div className="max-w-7xl mx-auto px-4 py-10 md:py-12">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info - Full width on mobile */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center mb-4">
              <Link to="/" className="bg-white rounded-lg p-2 shadow-lg hover:shadow-xl transition-shadow">
                <img
                  src="/pride-path-logo.jpg"
                  alt="Pride Path Commercial (pvt) ltd"
                  className="h-20 md:h-28 w-auto object-contain"
                />
              </Link>
            </div>
            <p className="text-gray-300 text-sm mb-4 leading-relaxed">
              Pride Path Commercial (pvt) ltd is a leading financial institution,
              providing comprehensive financial solutions across Sri Lanka.
            </p>
            <div className="flex flex-wrap gap-2">
              <a href="https://www.facebook.com/share/1AZn5LojD6/" target="_blank" rel="noopener noreferrer" className="bg-[#3b5998] p-2.5 rounded-full hover:opacity-80 transition-opacity min-h-[44px] min-w-[44px] flex items-center justify-center">
                <Facebook size={18} />
              </a>
              <span className="bg-[#1da1f2] p-2.5 rounded-full opacity-60 cursor-not-allowed min-h-[44px] min-w-[44px] flex items-center justify-center">
                <Twitter size={18} />
              </span>
              <span className="bg-gradient-to-br from-purple-600 to-pink-500 p-2.5 rounded-full opacity-60 cursor-not-allowed min-h-[44px] min-w-[44px] flex items-center justify-center">
                <Instagram size={18} />
              </span>
              <span className="bg-[#ff0000] p-2.5 rounded-full opacity-60 cursor-not-allowed min-h-[44px] min-w-[44px] flex items-center justify-center">
                <Youtube size={18} />
              </span>
              <span className="bg-[#0077b5] p-2.5 rounded-full opacity-60 cursor-not-allowed min-h-[44px] min-w-[44px] flex items-center justify-center">
                <Linkedin size={18} />
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-white text-sm transition-colors py-1 inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">{t('nav.products')}</h3>
            <ul className="space-y-2">
              {productCategories.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-white text-sm transition-colors py-1 inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - Full width on mobile */}
          <div className="col-span-2 md:col-span-1">
            <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">{t('nav.contactUs')}</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin size={18} className="text-[#2A9D8F] flex-shrink-0 mt-1" />
                <p className="text-gray-300 text-sm">{companyInfo.address}</p>
              </div>
              <a href={`tel:${companyInfo.phone}`} className="flex items-center space-x-3 text-gray-300 text-sm hover:text-white transition-colors py-1">
                <Phone size={18} className="text-[#2A9D8F] flex-shrink-0" />
                <span>{companyInfo.phone}</span>
              </a>
              <a href={`mailto:${companyInfo.email}`} className="flex items-center space-x-3 text-gray-300 text-sm hover:text-white transition-colors py-1">
                <Mail size={18} className="text-[#2A9D8F] flex-shrink-0" />
                <span>{companyInfo.email}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between text-xs md:text-sm text-gray-400 gap-3">
            <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
              <p className="text-center md:text-left">&copy; {new Date().getFullYear()} Pride Path Commercial (pvt) ltd. All Rights Reserved.</p>
              <span className="hidden md:block w-px h-3 bg-gray-700" />
              <p className="text-gray-400 font-medium tracking-wide">
                Powered by <a href="https://www.echomediaa.com/" target="_blank" rel="noopener noreferrer" className="text-[#2A9D8F] hover:underline transition-all">Echo Media</a>
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 md:gap-4">
              <Link to="/privacy-policy" className="hover:text-white transition-colors py-1">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors py-1">Terms & Conditions</Link>
              <Link to="/sitemap" className="hover:text-white transition-colors py-1">Sitemap</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
